import { useState, useEffect, useRef } from "react";

/**
 * DateFieldInput — tres campos numéricos independientes (DD / MM / AAAA)
 * Emite el valor como string "YYYY-MM-DD" (compatible con calculateAge y Firestore).
 * Reemplaza el <input type="date"> nativo para consistencia visual WE360.
 */
export function DateFieldInput({ label, value, onChange, onBlur, error, testId }) {
  const [day, setDay] = useState("");
  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");

  const monthRef = useRef(null);
  const yearRef = useRef(null);
  const containerRef = useRef(null);

  // Inicializar desde el valor guardado (formato "YYYY-MM-DD")
  useEffect(() => {
    if (value && typeof value === "string" && value.includes("-")) {
      const [y, m, d] = value.split("-");
      setYear(y || "");
      setMonth(m || "");
      setDay(d || "");
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const combine = (d, m, y) => {
    if (d.length === 2 && m.length === 2 && y.length === 4) {
      return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
    }
    return "";
  };

  const handleDay = (v) => {
    const clean = v.replace(/\D/g, "").slice(0, 2);
    setDay(clean);
    onChange(combine(clean, month, year));
    if (clean.length === 2) monthRef.current?.focus();
  };

  const handleMonth = (v) => {
    const clean = v.replace(/\D/g, "").slice(0, 2);
    setMonth(clean);
    onChange(combine(day, clean, year));
    if (clean.length === 2) yearRef.current?.focus();
  };

  const handleYear = (v) => {
    const clean = v.replace(/\D/g, "").slice(0, 4);
    setYear(clean);
    onChange(combine(day, month, clean));
  };

  const handleBlur = (e) => {
    if (!onBlur) return;
    // Solo llamar onBlur cuando el foco sale COMPLETAMENTE del grupo
    requestAnimationFrame(() => {
      if (!containerRef.current?.contains(document.activeElement)) {
        onBlur();
      }
    });
  };

  return (
    <div ref={containerRef} style={{ position: "relative", marginBottom: 22 }}>
      <label className="block text-xs font-semibold text-we-text mb-1.5">{label}</label>
      <div className="flex items-center gap-1.5">
        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          value={day}
          onChange={(e) => handleDay(e.target.value)}
          onBlur={handleBlur}
          placeholder="DD"
          maxLength={2}
          data-testid={`${testId}-day`}
          className={`we-input text-center ${error ? "we-input-error" : ""}`}
          style={{ flex: "0 0 58px", minWidth: 0 }}
        />
        <span className="text-we-text-2 font-bold text-sm select-none">/</span>
        <input
          ref={monthRef}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          value={month}
          onChange={(e) => handleMonth(e.target.value)}
          onBlur={handleBlur}
          placeholder="MM"
          maxLength={2}
          data-testid={`${testId}-month`}
          className={`we-input text-center ${error ? "we-input-error" : ""}`}
          style={{ flex: "0 0 58px", minWidth: 0 }}
        />
        <span className="text-we-text-2 font-bold text-sm select-none">/</span>
        <input
          ref={yearRef}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          value={year}
          onChange={(e) => handleYear(e.target.value)}
          onBlur={handleBlur}
          placeholder="AAAA"
          maxLength={4}
          data-testid={`${testId}-year`}
          className={`we-input text-center ${error ? "we-input-error" : ""}`}
          style={{ flex: "1 1 auto", minWidth: 0 }}
        />
      </div>
      {error && (
        <p
          data-testid={`${testId}-error`}
          style={{
            position: "absolute",
            bottom: -14,
            left: 4,
            fontSize: 11,
            lineHeight: 1,
            color: "#C0453A",
            fontWeight: 500,
            zIndex: 1,
            pointerEvents: "none",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: "calc(100% - 8px)",
          }}
        >
          {error}
        </p>
      )}
    </div>
  );
}
